import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import htmr from 'htmr';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { CircularProgress, ClickAwayListener } from '@mui/material';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import classes from './JobDetailsMobile.module.scss';
import GeneralInformation from '../JobDetails/components/GeneralInformation/GeneralInformation';
import { createLinkForSharingJob, getSerializedSearchParams, removeStyleAttribute } from '../../../../utils/helpers';
import MainButton from '../../../common/buttons/MainButton';
import { buttonVariants } from '../../../common/buttons/types';
import { removeSavedJob, saveJob } from '../../../../actions/jobs';
import colors from '../../../../scss/colors';
import { BookmarkIcon } from '../../../common/Icons';
import { loginRequest } from '../../../../authConfig';
import { setNotification } from '../../../../actions/appNotifications';
import { appNotificationsTypes } from '../../../../utils/constants';

import { subTabs, tabs } from '../../../../Routes/jobs/jobTabs';
import { saveCurrentJob, unsaveCurrentJob } from '../../../../actions/job';
import { usePrevious } from '../../../../utils/hooks';

const JobDetailsMobile = ({ job, fullJob = false, onClose = () => {} }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const dispatch = useDispatch();

  const currentJob = fullJob
    ? job
    : useSelector((state) => state.jobs.response.find((jobItem) => jobItem?.Id === job?.Id));
  const isSaving = useSelector((state) => state[fullJob ? 'job' : 'jobs'].pendingSave);

  const prevSaving = usePrevious(isSaving);
  const prevSaved = usePrevious(currentJob?.IsSaved);
  const [withShadow, setWithShadow] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const userId = localStorage.getItem('UserId');

  const scrollHandler = (e) => {
    if (e.target.scrollTop > 0 && !withShadow) setWithShadow(true);
    if (e.target.scrollTop === 0) setWithShadow(false);
  };

  const applyHandler = (id) => {
    onClose();
    localStorage.setItem(`OpenedJobId`, JSON.stringify(id));
    setSearchParams({ ...getSerializedSearchParams(), action: 'apply' });
  };

  const saveHandler = () => {
    const id = job.Id;
    if (isAuthenticated) {
      if (fullJob) {
        dispatch(currentJob?.IsSaved ? unsaveCurrentJob(id) : saveCurrentJob(id, userId));
        return;
      }
      dispatch(
        currentJob?.IsSaved
          ? removeSavedJob(id, searchParams.get('subTab') === subTabs.SAVED && searchParams.get('tab') === tabs.MY_JOBS)
          : saveJob(
              id,
              userId,
              searchParams.get('subTab') === subTabs.SAVED && searchParams.get('tab') === tabs.MY_JOBS
            )
      );
    } else {
      localStorage.setItem('saveJobAfterLogin', JSON.stringify(id));
      instance
        .loginRedirect({
          ...loginRequest,
          redirectUri: `${window.location.origin}/dashboard/jobs`,
          state: JSON.stringify({ navigate: `/dashboard/jobs/${id}` })
        })
        .catch((e) => {
          console.error(e);
        });
    }
  };

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  const shareHandler = () => {
    if (navigator.share) {
      navigator.share({
        text: job?.JobDescription,
        url: createLinkForSharingJob(window.location.origin, job.Id),
        title: job?.JobTitle
      });
    } else {
      dispatch(
        setNotification({
          type: appNotificationsTypes.SUCCESS,
          message: t('common.general.copied'),
          isAppStyled: true,
          bottom: '81px',
          duration: 3000
        })
      );
      navigator.clipboard.writeText(createLinkForSharingJob(window.location.origin, job.Id));
    }
    setAnchorEl(null);
  };

  useEffect(() => {
    if (currentJob?.IsSaved !== prevSaved && !isSaving && prevSaving) {
      dispatch(
        setNotification({
          type: appNotificationsTypes.SUCCESS,
          message: currentJob?.IsSaved ? t('dashboard.jobs.jobwassaved') : t('dashboard.jobs.jobwasunsaved'),
          isAppStyled: true,
          bottom: '81px',
          duration: 3000,
          action: currentJob?.IsSaved ? (
            <p
              role="presentation"
              className={classes.action}
              onClick={() => {
                window.location = `${window.location.origin}/dashboard/jobs?page=1&tab=${tabs.MY_JOBS}&subTab=${subTabs.SAVED}`;
              }}
            >
              {t('dashboard.jobs.seesavedjobs')}
            </p>
          ) : undefined
        })
      );
    }
  }, [isSaving]);

  useEffect(() => {
    localStorage.removeItem('OpenedJobId');
  }, []);

  return (
    <section className={classes.detailsContainer}>
      {fullJob ? (
        <div className={`${classes.headerFullJob} ${withShadow ? classes.withShadow : ''}`}>
          <div role="presentation" onClick={onClose}>
            <CloseRoundedIcon sx={{ color: colors.BLACK_TXT, fontSize: '24px' }} />
          </div>
          <div role="presentation" onClick={handleClick}>
            <MoreVertOutlinedIcon sx={{ color: colors.BLACK_TXT, fontSize: '24px' }} />
          </div>
        </div>
      ) : (
        <div className={`${classes.header} ${withShadow ? classes.withShadow : ''}`}>
          <div className={classes.swiper} />
          <div role="presentation" onClick={handleClick} className={classes.threeDot}>
            <MoreVertOutlinedIcon sx={{ color: colors.ICON_GREY, fontSize: '22px' }} />
          </div>
        </div>
      )}
      {anchorEl && (
        <ClickAwayListener onClickAway={handleClick}>
          <Popper
            id={id}
            open={!!open}
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: fullJob ? [15, -35] : [-4, -20]
                }
              }
            ]}
            sx={{ zIndex: 1200 }}
          >
            <Box
              onClick={shareHandler}
              sx={{
                width: 'max-content',
                height: fullJob ? 'max-content' : '51px',
                margin: '0 auto',
                padding: fullJob ? '30px 120px 30px 30px' : '11px 21px 12px 21px',
                borderRadius: fullJob ? '0 0 0 20px' : '10px',
                background: colors.PAGE_BACKGROUND,
                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                fontSize: '20px',
                fontWeight: '500',
                color: colors.MEDIUM_GREY,
                letterSpacing: '-0.4px'
              }}
            >
              {t('common.general.share')}
            </Box>
          </Popper>
        </ClickAwayListener>
      )}
      <div
        onScroll={scrollHandler}
        className={`${classes.body} ${fullJob && isAuthenticated ? classes.changedHeight : ''} ${
          fullJob && !isAuthenticated ? classes.changedHeightShortRoute : ''
        }`}
      >
        <Helmet>
          <title>{`${currentJob?.JobTitle} - ${currentJob?.Id} - Airswift`}</title>
        </Helmet>
        <div className={classes.generalWrapper}>
          <GeneralInformation job={job} />
        </div>
        <p className={classes.title}>{t('dashboard.jobs.jobdescription')}</p>
        <div className={classes.description}>{htmr(removeStyleAttribute(job.JobDescription))}</div>
      </div>
      <div className={classes.footer}>
        {currentJob?.IsOpen && (
          <MainButton
            rounded
            variant={currentJob?.IsApplied ? buttonVariants.DISABLED : buttonVariants.PRIMARY}
            disabled={currentJob?.IsApplied}
            action={() => {
              applyHandler(job?.Id);
            }}
            forceWidth="100%"
          >
            {currentJob?.IsApplied ? t('common.general.applied') : t('common.general.apply')}
          </MainButton>
        )}
        <MainButton variant={buttonVariants.DISABLED} outlined rounded action={saveHandler} forceWidth="100%">
          {isSaving && <CircularProgress size="16px" sx={{ color: colors.MEDIUM_GREY, marginRight: '4px' }} />}
          {currentJob?.IsSaved && !isSaving && <BookmarkIcon color={colors.MEDIUM_GREY} width="18" height="18" />}
          <span style={{ height: '19px', marginLeft: currentJob?.IsSaved ? '10px' : '0' }}>
            {currentJob?.IsSaved ? t('common.general.saved') : t('common.general.save')}
          </span>
        </MainButton>
      </div>
    </section>
  );
};

export default JobDetailsMobile;
